import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { TrendingUp, DollarSign, BarChart3, Check } from 'lucide-react'
import { PortfolioMode } from '../../types/investment'
import { useAuth } from '../../hooks/useAuth'
import SignInPrompt from '../SignInPrompt'

interface PortfolioModeSelectorProps {
  onSelectMode: (mode: 'beginner' | 'intermediate' | 'advanced') => void
  loading?: boolean
}

const PORTFOLIO_MODES: PortfolioMode[] = [
  {
    id: 'beginner',
    name: 'Beginner',
    description: 'Perfect for learning the basics of investing',
    startingBalance: 5000,
    features: ['Simplified interface', 'Educational tips', 'Risk warnings'],
    color: 'from-green-500 to-emerald-600'
  },
  {
    id: 'intermediate',
    name: 'Intermediate',
    description: 'Standard features for regular trading practice',
    startingBalance: 10000,
    features: ['Full trading features', 'Portfolio analytics', 'Market insights'],
    color: 'from-blue-500 to-cyan-600'
  },
  {
    id: 'advanced',
    name: 'Advanced',
    description: 'Professional-grade tools and analytics',
    startingBalance: 50000,
    features: ['Advanced analytics', 'Risk metrics', 'Backtesting tools'],
    color: 'from-purple-500 to-violet-600'
  }
]

const PortfolioModeSelector: React.FC<PortfolioModeSelectorProps> = ({ onSelectMode, loading = false }) => {
  const { user, isGuest } = useAuth()
  const [showSignInPrompt, setShowSignInPrompt] = useState(false)

  const handleModeSelect = (mode: 'beginner' | 'intermediate' | 'advanced') => {
    if (isGuest || !user) {
      setShowSignInPrompt(true)
      return
    }
    onSelectMode(mode)
  }
  const getModeIcon = (modeId: string) => {
    switch (modeId) {
      case 'beginner':
        return <TrendingUp className="w-6 h-6" />
      case 'intermediate':
        return <DollarSign className="w-6 h-6" />
      case 'advanced':
        return <BarChart3 className="w-6 h-6" />
      default:
        return <TrendingUp className="w-6 h-6" />
    }
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut'
      }
    }
  }

  return (
    <div className="min-h-screen bg-background-950 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="max-w-4xl w-full"
      >
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-background-100 mb-4">
            Choose Your Trading Mode
          </h1>
          <p className="text-xl text-background-400">
            Select the portfolio size and features that match your experience level
          </p>
        </motion.div>

        {/* Mode Cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid md:grid-cols-3 gap-6"
        >
          {PORTFOLIO_MODES.map((mode) => (
            <motion.div
              key={mode.id}
              variants={cardVariants}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="relative group cursor-pointer"
              onClick={() => !loading && handleModeSelect(mode.id)}
            >
              <div className={`bg-gradient-to-br ${mode.color} p-8 rounded-2xl shadow-xl border border-background-700 hover:border-background-600 transition-all duration-300`}>
                {/* Icon */}
                <div className="flex items-center justify-center w-16 h-16 bg-white/20 rounded-xl mb-6 mx-auto">
                  <div className="text-white">
                    {getModeIcon(mode.id)}
                  </div>
                </div>

                {/* Content */}
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-white mb-2">
                    {mode.name}
                  </h3>
                  <p className="text-white/90 mb-6">
                    {mode.description}
                  </p>

                  {/* Starting Balance */}
                  <div className="bg-white/20 rounded-lg p-4 mb-6">
                    <div className="text-3xl font-bold text-white mb-1">
                      ${mode.startingBalance.toLocaleString()}
                    </div>
                    <div className="text-white/80 text-sm">
                      Starting Balance
                    </div>
                  </div>

                  {/* Features */}
                  <div className="space-y-2 mb-6">
                    {mode.features.map((feature, index) => (
                      <div key={index} className="flex items-center text-white/90">
                        <Check className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Select Button */}
                  <button
                    disabled={loading}
                    className="w-full bg-white text-gray-900 font-semibold py-3 px-6 rounded-lg hover:bg-white/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Setting up...' : `Start with ${mode.name}`}
                  </button>
                </div>
              </div>

              {/* Hover Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-background-900/50 to-background-800/50 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
            </motion.div>
          ))}
        </motion.div>

        {/* Footer Note */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center mt-8"
        >
          <p className="text-background-500 text-sm">
            You can change your mode later in settings. All trading is virtual with no real money at risk.
          </p>
        </motion.div>
      </motion.div>

      {/* Sign In Prompt */}
      <SignInPrompt
        isOpen={showSignInPrompt}
        onClose={() => setShowSignInPrompt(false)}
        title="Sign in to start investing"
        message="Create an account to set up your virtual portfolio and start paper trading with real market data."
        action="Sign In to Invest"
      />
    </div>
  )
}

export default PortfolioModeSelector
